import {Input} from 'antd'
import Pure from 'components/Pure'
import _ from 'lodash'
import useTranslate from 'modules/local/useTranslate'
import {useHistory} from 'modules/navigation/useRouter'
import React, {useCallback, useEffect, useMemo, useState} from 'react'
import {useLocation, useToggle} from 'react-use'
import {NullAsDefault, renderElse, renderOwnChild, renderSelf} from 'views/Shared'
import {DefaultAdvancedToggle, ExpandablePanel,} from '../custom/AdvancedFormGroup'
import EntityList, {EntityPaginationMeta} from '../EntityList'
import classNames from "classnames"
import {HiMiniAdjustmentsHorizontal} from "react-icons/hi2";
import {getRouteSearchParams, useRouteSearchParam} from "../../../envs/_current/history";
import {renderIf} from "../../Shared";
import {BsCaretDown, BsCaretUpFill} from "react-icons/bs";

const EmptySortBy = ({value, onSelect}) => null

const EmptyAdvancedFilter = ({params, value, onChange}) => null

const renderDefaultSort = (child, {title}) => (
    renderIf(
        title,
        <div className="flex items-center justify-between">
            <span className="font-semibold text-sm text-color-400 uppercase tracking-wider">
                {title}
            </span>
            <div className="flex items-end justify-end">
                {child}
            </div>
        </div>
    )
)

export const createPagerKey = (keys = []) => {
    const array = Array.from(keys || []).map((e) =>
        _.isString(e) ? e : JSON.stringify(e)
    )
    return ({sortBy, keyword, filterBy}) =>
        [sortBy, keyword, ...array, JSON.stringify(filterBy)].join('/')
}

export const handleFilter =
    ({searchParams, onFilter = NullAsDefault, filter_actions = {}}) =>
        (params = {}) => {
            let search_params = new URLSearchParams(searchParams)

            Object.keys(params).forEach((name) => {
                const param_value = params[name]
                const filter_name = _.get(filter_actions, name)
                if (param_value && filter_name) {
                    search_params.set(filter_name, param_value)
                } else {
                    search_params.delete(filter_name)
                }
            })

            onFilter({
                search: '?' + search_params.toString(),
            })
        }

const ListFilter = ({
                        filter,
                        sortBy,
                        params,
                        keyword,
                        onSearch = NullAsDefault,
                        noHeader = false,
                        sortHeader = null,
                        onSortChange = NullAsDefault,
                        onFilterChange = NullAsDefault,
                        isToggleDefault = false,
                        Wrapper = renderOwnChild,
                        renderSearchBar = renderSelf,
                        searchBarSize = "middle",
                        SortByWidget = EmptySortBy,
                        renderSortBy = renderDefaultSort,
                        AdvancedFilter = EmptyAdvancedFilter,
                        AdvancedToggle = DefaultAdvancedToggle,
                        renderFilter = (child, filter_widget) => child,
                        renderHeader = ([searchBar, filterBar]) => (
                            <React.Fragment>
                                {filterBar}
                                {searchBar}
                            </React.Fragment>
                        ),
                    }) => {
    const t = useTranslate()

    const [isToggle, toggle] = useToggle(isToggleDefault)

    useEffect(() => {
        if (_.some(Object.values(filter), (value) => !!value)) {
            toggle(true)
        }
    }, [filter])

    const filter_widget = (
        <AdvancedFilter
            value={filter}
            onChange={onFilterChange}
            {...(params ?? {})}
        />
    )

    const search_bar = renderSearchBar(
        <Input.Search
            defaultValue={keyword}
            onSearch={(value) => onSearch(value)}
            allowClear={true}
            size={searchBarSize}
            className="input-search-filter"
            placeholder={t('search')}
        />
    )

    const filter_bar = renderFilter(
        <div
            className={classNames(
                "rounded-lg border-color-50 mt-1 mb-2",
                isToggle ? "pl-4 pr-2 pt-1 pb-4 border background shadow-md" : "border-b px-2 hover:shadow-md hover:background"
            )}>
            <ExpandablePanel
                onClick={toggle}
                Header={AdvancedToggle}
                expanded={isToggle}
                icons={[BsCaretDown, BsCaretUpFill]}
                prefixIcon={<HiMiniAdjustmentsHorizontal size={13}/>}
                title="advanced filter">
                {filter_widget}
            </ExpandablePanel>
        </div>,
        filter_widget
    )

    return (
        <Wrapper>
            {renderElse(
                noHeader,
                <div className="flex flex-col space-y-2">
                    {renderHeader([search_bar, filter_bar])}
                </div>
            )}
            {renderSortBy(
                <SortByWidget
                    value={sortBy ?? undefined}
                    onSelect={onSortChange}
                />,
                {title: sortHeader}
            )}
        </Wrapper>
    )
}

const usePaginationHelper = ({
                                 noHeader,
                                 pagerInfo,
                                 sortHeader,
                                 renderList,
                                 renderFilter,
                                 renderSortBy,
                                 renderHeader,
                                 SortByWidget,
                                 filter_params,
                                 onInit = NullAsDefault,
                                 AdvancedFilter,
                                 AdvancedToggle,
                                 compact = false,
                                 renderSearchBar,
                                 searchBarSize = "middle",
                                 dependencies = [],
                                 query_params = {
                                     sortBy: 'sort-by',
                                     keyword: 'keyword',
                                 },
                                 withKey = renderSelf,
                                 withValues = renderSelf,
                                 isToggleDefault = false,
                                 handleFilter: handleCustomFilter,
                             }) => {
    const history = useHistory()

    const location = useLocation()

    const [sortBy, setSortBy] = useState()

    const [filterBy, setFilterBy] = useState({})

    const {sortBy: sort_query, keyword: keyword_query} = query_params ?? {}

    const keyword = useRouteSearchParam(keyword_query)


    const handleSearch = useCallback(
        (keyword) => {
            let search_params = getRouteSearchParams()
            if (keyword) {
                search_params.set(keyword_query, keyword)
            } else {
                search_params.delete(keyword_query)
            }
            history.push({
                search: '?' + search_params.toString(),
                hash: location.hash,
            })
        },
        [location.search, location.hash]
    )

    useEffect(() => {
        onInit({
            onSortChange: setSortBy,
            onFilterChange: true
                ? setFilterBy
                : (values) => {
                    setFilterBy(_.omitBy(values, _.isEmpty))
                },
        })
    }, [location.search, location.hash, location.state, ...dependencies])

    const onSortChange = useCallback(
        (value) => {
            let search_params = getRouteSearchParams()
            if (value) {
                search_params.set(sort_query, value)
            } else {
                search_params.delete(sort_query)
            }
            history.push({
                search: '?' + search_params.toString(),
            })
        },
        [location.search, location.hash]
    )

    const refreshKey = withKey({
        sortBy,
        keyword,
        filterBy,
    })

    const [widget_header, widget, data_widget] = useMemo(() => {
        const data = (
            <Pure
                input={[
                    sortBy,
                    keyword,
                    filterBy,
                    renderList,
                    refreshKey,
                    dependencies,
                ]}>
                {_.isFunction(renderList) ? (
                    renderList({
                        sortBy,
                        keyword,
                        filterBy,
                        refreshKey,
                    })
                ) : (
                    <EntityList
                        key={refreshKey}
                        Header={({meta}) => meta}
                        PaginationMeta={EntityPaginationMeta}
                        values={withValues({
                            keyword,
                            sort_by: sortBy,
                            ...(filterBy ?? {}),
                        })}
                        {...(pagerInfo ?? {})}
                    />
                )}
            </Pure>
        )
        const header = !compact ? (
            <ListFilter
                {...{
                    sortBy,
                    keyword,
                    noHeader,
                    sortHeader,
                    renderHeader,
                    SortByWidget,
                    renderFilter,
                    renderSortBy,
                    onSortChange,
                    AdvancedFilter,
                    AdvancedToggle,
                    renderSearchBar,
                    searchBarSize,
                    isToggleDefault,
                }}
                filter={filterBy}
                params={filter_params}
                onSearch={handleSearch}
                onFilterChange={
                    _.isFunction(handleCustomFilter)
                        ? handleCustomFilter
                        : handleFilter({
                            searchParams: location.search,
                            filter_actions: query_params,
                            onFilter: ({search}) =>
                                history.push({
                                    search,
                                }),
                        })
                }
            />
        ) : null;

        return [
            header,
            <React.Fragment>
                {header}
                {data}
            </React.Fragment>,
            data,
        ]
    }, [
        compact,
        keyword,
        sortBy,
        filterBy,
        refreshKey,
        renderList,
        renderHeader,
        location.search,
        location.hash,
    ])

    return [
        widget,
        {
            sortBy,
            keyword,
            filterBy,
            setSortBy,
            data_widget,
            setFilterBy,
            handleSearch,
            widget_header,
            onFilterChange: (params) =>
                _.isFunction(handleCustomFilter)
                    ? handleCustomFilter
                    : handleFilter({
                        searchParams: location.search,
                        filter_actions: query_params,
                        onFilter: ({search}) =>
                            history.push({
                                search,
                            }),
                    })(params),
        },
    ]
}

export default usePaginationHelper
